/**
 * frontend/src/pages/valuation/_snapshotDiff.ts
 *
 * VAL-VERSION-DIFF-1 — what changed between two signed versions' frozen
 * inputs. `SnapshotBody.inputs` is `inputs_to_dict()`
 * (core/valuation/finalize_codec.py); `range_low`/`range_high` live on the
 * body itself, not under `inputs`. Pure comparison — no fetch, no math.
 *
 * VAL-VERSION-DIFF-1b: `range_low`/`range_high` are the benchmark MULTIPLE
 * range (e.g. 2.5x), not dollars — verified live on DEV. The dollar band is
 * `value_low`/`value_high`, the point value is `calculated_value`.
 */
import type { SnapshotBody } from '@/types/valuationB3'
import { fracPct, money, multiple, pct } from '@/utils/format'

export interface SnapshotDiffRow {
  field: string
  label: string
  before: string
  after: string
}

const MONEY_KEYS = new Set([
  'normalized_earnings',
  'income_value',
  'total_assets',
  'total_liabilities',
  'fair_value_adjustments',
  'base_cash_flow',
  'replacement_compensation',
  'value_low',
  'value_high',
  'calculated_value',
])

const MULTIPLE_KEYS = new Set([
  'range_low',
  'range_high',
  'concluded_multiple',
])

const PCT_KEYS = new Set([
  'risk_free_rate',
  'company_specific_risk',
  'long_term_growth_rate',
  'equity_risk_premium',
  'size_premium',
  'growth_rate',
])

const FRAC_PCT_KEYS = new Set([
  'capitalized_earnings_weight',
])

const LABELS: Record<string, string> = {
  benchmark_key: 'Benchmark',
  normalized_earnings: 'Normalized earnings',
  basis: 'Basis',
  income_value: 'Income value',
  total_assets: 'Total assets',
  total_liabilities: 'Total liabilities',
  fair_value_adjustments: 'Fair value adjustments',
  override_multiple: 'Override multiple',
  override_rationale: 'Override rationale',
  override_disposition: 'Override disposition',
  naics_code: 'NAICS code',
  sub_vertical: 'Sub-vertical',
  market_only_acknowledged: 'Market-only acknowledged',
  risk_free_rate: 'Risk-free rate',
  company_specific_risk: 'Company-specific risk',
  long_term_growth_rate: 'Long-term growth rate',
  equity_risk_premium: 'Equity risk premium',
  size_premium: 'Size premium',
  growth_rate: 'Growth rate',
  projection_years: 'Projection years',
  capitalized_earnings_weight: 'Capitalized earnings weight',
  base_cash_flow: 'Base cash flow',
  replacement_compensation: 'Replacement compensation',
  range_low: 'Benchmark range low',
  range_high: 'Benchmark range high',
  value_low: 'Value band low',
  value_high: 'Value band high',
  calculated_value: 'Calculated value',
  concluded_multiple: 'Concluded multiple',
}

function labelFor(key: string): string {
  return LABELS[key] ?? key
}

function formatValue(key: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (MONEY_KEYS.has(key)) return typeof value === 'number' ? money(value) : String(value)
  if (MULTIPLE_KEYS.has(key)) return typeof value === 'number' ? multiple(value) : String(value)
  if (PCT_KEYS.has(key)) return typeof value === 'number' ? pct(value) : String(value)
  if (FRAC_PCT_KEYS.has(key)) return typeof value === 'number' ? fracPct(value) : String(value)
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return String(value)
}

function sameValue(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

/** Compares two frozen snapshots' inputs (plus the range) and returns only
 * what changed — an unchanged pair returns an empty array. */
export function diffSnapshots(prev: SnapshotBody, next: SnapshotBody): SnapshotDiffRow[] {
  const rows: SnapshotDiffRow[] = []
  const prevInputs = (prev.inputs ?? {}) as Record<string, unknown>
  const nextInputs = (next.inputs ?? {}) as Record<string, unknown>

  const inputKeys = new Set([...Object.keys(prevInputs), ...Object.keys(nextInputs)])
  for (const key of inputKeys) {
    if (key === 'answers') continue // diffed separately, per question id
    const before = prevInputs[key]
    const after = nextInputs[key]
    if (sameValue(before, after)) continue
    rows.push({ field: key, label: labelFor(key), before: formatValue(key, before), after: formatValue(key, after) })
  }

  const prevAnswers = (prevInputs.answers ?? {}) as Record<string, unknown>
  const nextAnswers = (nextInputs.answers ?? {}) as Record<string, unknown>
  const answerKeys = new Set([...Object.keys(prevAnswers), ...Object.keys(nextAnswers)])
  for (const questionId of answerKeys) {
    const before = prevAnswers[questionId]
    const after = nextAnswers[questionId]
    if (sameValue(before, after)) continue
    rows.push({
      field: `answers.${questionId}`,
      label: labelFor(questionId),
      before: formatValue(questionId, before),
      after: formatValue(questionId, after),
    })
  }

  if (prev.range_low !== next.range_low) {
    rows.push({
      field: 'range_low',
      label: labelFor('range_low'),
      before: formatValue('range_low', prev.range_low),
      after: formatValue('range_low', next.range_low),
    })
  }
  if (prev.range_high !== next.range_high) {
    rows.push({
      field: 'range_high',
      label: labelFor('range_high'),
      before: formatValue('range_high', prev.range_high),
      after: formatValue('range_high', next.range_high),
    })
  }
  if (prev.value_low !== next.value_low) {
    rows.push({
      field: 'value_low',
      label: labelFor('value_low'),
      before: formatValue('value_low', prev.value_low),
      after: formatValue('value_low', next.value_low),
    })
  }
  if (prev.value_high !== next.value_high) {
    rows.push({
      field: 'value_high',
      label: labelFor('value_high'),
      before: formatValue('value_high', prev.value_high),
      after: formatValue('value_high', next.value_high),
    })
  }
  if (prev.calculated_value !== next.calculated_value) {
    rows.push({
      field: 'calculated_value',
      label: labelFor('calculated_value'),
      before: formatValue('calculated_value', prev.calculated_value),
      after: formatValue('calculated_value', next.calculated_value),
    })
  }
  if (prev.concluded_multiple !== next.concluded_multiple) {
    rows.push({
      field: 'concluded_multiple',
      label: labelFor('concluded_multiple'),
      before: formatValue('concluded_multiple', prev.concluded_multiple),
      after: formatValue('concluded_multiple', next.concluded_multiple),
    })
  }

  return rows
}
